import React, { ReactElement, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Pencil } from 'lucide-react';

import { Button } from '@ui/components/ui/button';
import { Input } from '@ui/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  selectItemsByValue,
} from '@ui/components/ui/select';
import { Textarea } from '@ui/components/ui/textarea';

import { DetailRow } from '@app/components/core';
import { cardClass } from '@app/constants/styles';
import { EditSheet } from '@app/components/core/edit-sheet';
import { useIsMobile } from '@ui/hooks/use-mobile';
import { ProductEntity } from '@data';
import { useRndDossier, useUpdateRndDossier } from '@data/hooks/products';

import { collaborationFrom, emptyDraft, RndDossierDraft, toDossier, toDraft, UNANSWERED } from './rndDossierDraft';

/** The five descriptions the exemption is claimed with, in the order the filing asks for them. */
const SECTIONS = ['context', 'existingKnowledge', 'advancement', 'obstacles', 'activities'] as const;

/** Beyond this a field is folded away: about two lines of prose, which is as much as the row shows. */
const FOLD_ABOVE = 180;

/** A written field, kept to a readable measure and folded until asked for. */
function FieldText({ text }: { text: string }): ReactElement {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const value = text.trim();

  if (!value) return <span className="text-muted-foreground">{t('taxExemption.notWritten')}</span>;

  const folds = value.length > FOLD_ABOVE;
  // Blank lines go when folded: they would eat the two lines the row shows and strand the ellipsis.
  const folded = value.replace(/\s*\n\s*/g, ' ');

  return (
    <div>
      <p className={folds && !open ? 'line-clamp-2 leading-relaxed' : 'whitespace-pre-line leading-relaxed'}>
        {folds && !open ? folded : value}
      </p>
      {folds ? (
        <button
          type="button"
          onClick={() => setOpen(prev => !prev)}
          className="mt-1 text-xs font-semibold text-muted-foreground transition-colors hover:text-foreground"
        >
          {open ? t('showLess') : t('showMore')}
        </button>
      ) : null}
    </div>
  );
}

/** The R&D tax exemption dossier. Internal by definition, so it never renders for a client. */
function RndDossierCard({ product, canEdit }: { product: ProductEntity; canEdit: boolean }): ReactElement {
  const { t } = useTranslation();

  const { data: dossier, isPending, isError } = useRndDossier(product.id);
  const { mutate: save, isPending: isSaving } = useUpdateRndDossier();

  const [draft, setDraft] = useState<RndDossierDraft>(emptyDraft);
  const [editing, setEditing] = useState(false);
  const [failed, setFailed] = useState<string | null>(null);

  // Only while the form is closed: an answer arriving mid-edit would overwrite what is being typed.
  useEffect(() => {
    if (!editing) setDraft(toDraft(dossier));
  }, [dossier, editing]);

  const collaborationItems = selectItemsByValue([
    { value: UNANSWERED, label: t('taxExemption.notWritten') },
    { value: 'yes', label: t('yes') },
    { value: 'no', label: t('no') },
  ]);

  const view = toDraft(dossier);

  const startEditing = (): void => {
    setFailed(null);
    setEditing(true);
  };

  const stopEditing = (): void => {
    setDraft(view);
    setFailed(null);
    setEditing(false);
  };

  const onSave = (): void => {
    setFailed(null);
    save(
      { productId: product.id, values: toDossier(draft) },
      {
        onSuccess: () => setEditing(false),
        // Kept open with what was typed still in it: a save that failed has nothing to show yet.
        onError: error => {
          const status = (error as { response?: { status?: number } })?.response?.status;
          setFailed(status === 400 ? t('productDetail.saveRejected') : t('productDetail.saveFailed'));
        },
      },
    );
  };

  // Whole numbers only, because the API validates the share with `@IsInt`.
  const share = Number(draft.rndShare);
  const shareOutOfRange = draft.rndShare !== '' && (!Number.isInteger(share) || share < 0 || share > 100);

  const isMobile = useIsMobile();
  // A phone edits in a sheet (below), leaving the card as it reads.
  const inline = editing && !isMobile;

  const cardActions = (() => {
    if (!canEdit) return null;
    if (!inline) {
      return (
        <Button type="button" variant="outline" size="sm" disabled={isPending || isError} onClick={startEditing}>
          <Pencil className="size-4" />
          {t('edit')}
        </Button>
      );
    }
    return (
      <div className="flex gap-2">
        <Button type="button" variant="ghost" size="sm" disabled={isSaving} onClick={stopEditing}>
          {t('actions.cancel')}
        </Button>
        <Button type="button" size="sm" disabled={shareOutOfRange || isSaving} onClick={onSave}>
          {t('actions.save')}
        </Button>
      </div>
    );
  })();

  const rows = (edit: boolean): ReactElement => (
    <dl className="divide-y divide-border">
      <DetailRow label={t('taxExemption.rndShare')}>
        {edit ? (
          <div className="flex items-center gap-1.5">
            <Input
              type="number"
              min={0}
              max={100}
              step={1}
              aria-label={t('taxExemption.rndShare')}
              value={draft.rndShare}
              onChange={e => setDraft(prev => ({ ...prev, rndShare: e.target.value }))}
              className="w-20 bg-popover"
            />
            <span className="text-sm text-muted-foreground">%</span>
          </div>
        ) : (
          <span className="font-semibold text-foreground">
            {view.rndShare === '' ? <span className="text-muted-foreground">-</span> : `${view.rndShare}%`}
          </span>
        )}
      </DetailRow>

      <DetailRow label={t('taxExemption.academicCollaboration')}>
        {edit ? (
          <Select
            items={collaborationItems}
            value={draft.academicCollaboration === '' ? UNANSWERED : draft.academicCollaboration}
            onValueChange={value => setDraft(prev => ({ ...prev, academicCollaboration: collaborationFrom(value) }))}
          >
            <SelectTrigger aria-label={t('taxExemption.academicCollaboration')} className="w-36 max-w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={UNANSWERED}>{t('taxExemption.notWritten')}</SelectItem>
              <SelectItem value="yes">{t('yes')}</SelectItem>
              <SelectItem value="no">{t('no')}</SelectItem>
            </SelectContent>
          </Select>
        ) : (
          <span className="font-semibold text-foreground">
            {view.academicCollaboration === '' ? (
              <span className="font-normal text-muted-foreground">{t('taxExemption.notWritten')}</span>
            ) : (
              t(view.academicCollaboration)
            )}
          </span>
        )}
      </DetailRow>

      {SECTIONS.map(key => (
        <DetailRow key={key} label={t(`taxExemption.sections.${key}.label`)}>
          {edit ? (
            <div className="max-w-[46rem]">
              <Textarea
                aria-label={t(`taxExemption.sections.${key}.label`)}
                rows={5}
                value={draft[key]}
                onChange={e => setDraft(prev => ({ ...prev, [key]: e.target.value }))}
                className="bg-popover"
              />
              <p className="mt-1 text-xs text-muted-foreground">{t(`taxExemption.sections.${key}.hint`)}</p>
            </div>
          ) : (
            <div className="max-w-[46rem]">
              <FieldText text={view[key]} />
            </div>
          )}
        </DetailRow>
      ))}
    </dl>
  );

  return (
    <>
      <div data-testid="rnd-dossier-card" className={`${cardClass} mb-6`}>
        <div className="mb-5 flex items-center justify-between gap-4">
          <h2 className="text-lg font-bold text-foreground">{t('taxExemption.title')}</h2>
          {cardActions}
        </div>

        {isError ? <p className="text-sm text-muted-foreground">{t('taxExemption.loadFailed')}</p> : rows(inline)}

        {inline && shareOutOfRange ? (
          <p className="mt-4 text-sm text-destructive">{t('productDetail.shareOutOfRange')}</p>
        ) : null}
        {inline && failed ? <p className="mt-4 text-sm text-destructive">{failed}</p> : null}
      </div>
      <EditSheet
        open={editing && isMobile}
        title={t('taxExemption.title')}
        onCancel={stopEditing}
        onSubmit={event => {
          event.preventDefault();
          onSave();
        }}
        isPending={isSaving}
        canSave={!shareOutOfRange}
        error={shareOutOfRange ? t('productDetail.shareOutOfRange') : failed}
      >
        {rows(true)}
      </EditSheet>
    </>
  );
}

export default RndDossierCard;
